import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  Popover,
  Text,
} from "@chakra-ui/react";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useState } from "react";

const monthNames = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

type Props = {
  setValue: (period: string | undefined) => void;
  placeholder?: string;
  minYear?: number;
  maxYear?: number;
  value?: string | undefined;
  isDisabled?: boolean;
  isClearable?: boolean;
};

const YearMonthPicker = ({
  setValue,
  placeholder = "Seleccionar periodo",
  minYear = new Date().getFullYear() - 1,
  maxYear = new Date().getFullYear() + 1,
  value = undefined,
  isDisabled = false,
  isClearable = true,
}: Props) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string | undefined>(
    undefined
  );
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [isOpen, setIsOpen] = useState(false);

  const formatPeriod = (year: number, month: number) => {
    return `${year}-${month.toString().padStart(2, "0")}`;
  };

  const handlePeriodSelect = ({
    year,
    month,
  }: {
    year: number;
    month: number;
  }) => {
    setSelectedPeriod(formatPeriod(year, month));
    setValue(formatPeriod(year, month));
    setIsOpen(false);
  };

  const yearOf = (period: string | undefined): number | undefined => {
    if (!period) return undefined;
    const parsed = Number(period.split("-")[0]);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const handleOpenChange = (details: { open: boolean }) => {
    if (details.open) {
      const selectedYear = yearOf(selectedPeriod);
      if (selectedYear !== undefined) setYear(selectedYear);
    }
    setIsOpen(details.open);
  };

  useEffect(() => {
    setSelectedPeriod(value);
    const selectedYear = yearOf(value);
    if (selectedYear !== undefined) setYear(selectedYear);
  }, [value]);

  const isMonthSelected = (month: string) => {
    return selectedPeriod === formatPeriod(year, monthNames.indexOf(month) + 1);
  };

  const isMonthCurrent = (month: string) => {
    const currentPeriod = formatPeriod(
      new Date().getFullYear(),
      new Date().getMonth() + 1
    );
    return currentPeriod === formatPeriod(year, monthNames.indexOf(month) + 1);
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Box position="relative" width="100%">
        <Popover.Trigger asChild>
          <Box position="relative">
            <Input
              size="sm"
              placeholder={placeholder}
              disabled={isDisabled}
              _placeholder={{ color: "#5D5C60" }}
              readOnly
              cursor="pointer"
              pl={4}
              pr={10}
              value={selectedPeriod ?? ""}
            />
            <Box
              position="absolute"
              right={3}
              top="50%"
              transform="translateY(-50%)"
              zIndex={1}
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (isDisabled) return;
                if (isClearable && selectedPeriod) {
                  setSelectedPeriod(undefined);
                  setValue(undefined);
                  setIsOpen(false);
                  return;
                }
                setIsOpen(true);
              }}
            >
              {isClearable && selectedPeriod ? (
                <X size={16} />
              ) : (
                <Calendar size={16} />
              )}
            </Box>
          </Box>
        </Popover.Trigger>

        <Popover.Positioner>
          <Popover.Content minW="240px" p={2}>
            <Flex justify="space-between" align="center" mb={4}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setYear(year - 1)}
                disabled={year === minYear}
              >
                <ChevronLeft />
              </Button>
              <Text fontWeight="medium">{year}</Text>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setYear(year + 1)}
                disabled={year === maxYear}
              >
                <ChevronRight />
              </Button>
            </Flex>
            <Grid templateColumns="repeat(3, 1fr)" gap={1}>
              {monthNames.map((month) => (
                <GridItem key={month} textAlign="center" fontSize="sm">
                  <Button
                    bg={
                      isMonthSelected(month)
                        ? "palomma.500"
                        : isMonthCurrent(month)
                        ? "gray.100"
                        : undefined
                    }
                    style={{
                      borderRadius: "0.5rem",
                    }}
                    _hover={{
                      bg: isMonthSelected(month) ? "palomma.600" : "gray.400",
                    }}
                    color={
                      isMonthSelected(month)
                        ? "white"
                        : isMonthCurrent(month)
                        ? "black"
                        : undefined
                    }
                    variant="ghost"
                    size="sm"
                    width="100%"
                    onClick={() =>
                      handlePeriodSelect({
                        year,
                        month: monthNames.indexOf(month) + 1,
                      })
                    }
                  >
                    {month}
                  </Button>
                </GridItem>
              ))}
            </Grid>
          </Popover.Content>
        </Popover.Positioner>
      </Box>
    </Popover.Root>
  );
};

export default YearMonthPicker;
